import React, { useEffect, useRef, useState } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import { useTranslation } from "react-i18next";
import { Location } from "../../../@types/DTO/Locations";
import { BookingGetters } from "../../../store/onlineBooking/getters";
import { hasValidGpsCoordinates } from "../../../helpers/hasValidGpsCoordinates";

export interface BranchWithDistance {
  location: Location;
  distanceKm: number;
  rank: number; // 0 = nearest
}

interface BranchAddressSearchProps {
  locations: Location[];
  onAddressSelected: (
    address: string,
    coords: { lat: number; lng: number },
    ranked: BranchWithDistance[],
    distanceMap: Record<string, number>
  ) => void;
  onAddressCleared: () => void;
  initialAddress?: string;
  primaryColor?: string;
  secondaryColor?: string;
  label?: string;
}

const BranchAddressSearch: React.FC<BranchAddressSearchProps> = ({
  locations,
  onAddressSelected,
  onAddressCleared,
  initialAddress = "",
  label,
}) => {
  const { t: translate } = useTranslation();
  const appSettings = BookingGetters.getAppSettings();
  const fontFamily = appSettings?.fontFamily ?? undefined;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<any>(null);
  const [addressValue, setAddressValue] = useState<string>(initialAddress);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (initialAddress && !addressValue) {
      setAddressValue(initialAddress);
    }
  }, [initialAddress]);

  const locationsRef = useRef(locations);
  locationsRef.current = locations;

  const onAddressSelectedRef = useRef(onAddressSelected);
  onAddressSelectedRef.current = onAddressSelected;

  const onAddressClearedRef = useRef(onAddressCleared);
  onAddressClearedRef.current = onAddressCleared;

  const calculateDistancesRef =
    useRef<(origin: { lat: number; lng: number }, address: string) => void>();

  // ─── Initialise Google Places Autocomplete ──────────────────────────────────
  useEffect(() => {
    if (!inputRef.current || !window.google?.maps?.places) return;

    const autocomplete = new window.google.maps.places.Autocomplete(
      inputRef.current,
      { types: ["address"] }
    );

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.geometry?.location) return;

      const address = place.formatted_address || inputRef.current?.value || "";
      const coords = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };

      setAddressValue(address);
      calculateDistancesRef.current?.(coords, address);
    });

    autocompleteRef.current = autocomplete;

    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(
          autocompleteRef.current
        );
      }
    };
  }, []);

  // Distance Matrix
  const calculateDistances = (
    origin: { lat: number; lng: number },
    address: string
  ) => {
    const locationsWithCoords = locationsRef.current.filter(
      hasValidGpsCoordinates
    );

    if (!locationsWithCoords.length || !window.google?.maps) {
      onAddressSelectedRef.current(address, origin, [], {});
      return;
    }

    setLoading(true);

    const service = new window.google.maps.DistanceMatrixService();

    service.getDistanceMatrix(
      {
        origins: [{ lat: origin.lat, lng: origin.lng }],
        destinations: locationsWithCoords.map((loc) => ({
          lat: loc.latitude,
          lng: loc.longitude,
        })),
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        setLoading(false);

        if (
          status !== window.google.maps.DistanceMatrixStatus.OK ||
          !response?.rows?.[0]?.elements
        ) {
          // Fallback: use Haversine straight-line distances
          const ranked = computeHaversineRanking(origin, locationsWithCoords);
          const distMap = buildDistanceMap(ranked);
          onAddressSelectedRef.current(address, origin, ranked, distMap);
          return;
        }

        const elements = response.rows[0].elements;

        // Map each element back to its location
        const withDistances: { location: Location; distanceM: number }[] =
          locationsWithCoords
            .map((loc, i) => {
              const el = elements[i];
              // If a route could not be found, fall back to Haversine
              const distanceM =
                el.status === "OK" && el.distance?.value != null
                  ? el.distance.value
                  : haversineMeters(
                      origin.lat,
                      origin.lng,
                      loc.latitude,
                      loc.longitude
                    );
              return { location: loc, distanceM };
            })
            .sort((a, b) => a.distanceM - b.distanceM);

        const ranked: BranchWithDistance[] = withDistances.map(
          ({ location, distanceM }, idx) => ({
            location,
            distanceKm: Math.round(distanceM / 1000),
            rank: idx,
          })
        );

        const distMap: Record<string, number> = {};
        ranked.forEach(({ location, distanceKm }) => {
          distMap[location.locationCode] = distanceKm;
        });

        onAddressSelectedRef.current(address, origin, ranked, distMap);
      }
    );
  };

  // Keep the ref in sync with the latest calculateDistances on every render
  calculateDistancesRef.current = calculateDistances;

  // Helpers
  const haversineMeters = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number => {
    const R = 6371000;
    const toRad = (v: number) => (v * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const computeHaversineRanking = (
    origin: { lat: number; lng: number },
    locs: Location[]
  ): BranchWithDistance[] =>
    locs
      .map((loc) => ({
        location: loc,
        distanceM: haversineMeters(
          origin.lat,
          origin.lng,
          loc.latitude,
          loc.longitude
        ),
      }))
      .sort((a, b) => a.distanceM - b.distanceM)
      .map(({ location, distanceM }, idx) => ({
        location,
        distanceKm: Math.round(distanceM / 1000),
        rank: idx,
      }));

  const buildDistanceMap = (
    ranked: BranchWithDistance[]
  ): Record<string, number> => {
    const m: Record<string, number> = {};
    ranked.forEach(({ location, distanceKm }) => {
      m[location.locationCode] = distanceKm;
    });
    return m;
  };

  // Handle manual clear
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setAddressValue(val);
    if (!val) {
      onAddressClearedRef.current();
    }
  };

  // Geolocation
  const handleGeolocate = () => {
    if (!navigator.geolocation) return;
    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        if (window.google?.maps?.Geocoder) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results?.[0]) {
              const address = results[0].formatted_address;
              setAddressValue(address);
              setLoading(false);
              calculateDistancesRef.current?.({ lat, lng }, address);
            } else {
              setLoading(false);
            }
          });
        } else {
          setLoading(false);
        }
      },
      () => {
        setLoading(false);
      }
    );
  };

  return (
    <div className="w-full">
      <TextField
        fullWidth
        label={label ?? translate("Search nearby pickup branches")}
        placeholder={translate("Enter your address...")}
        value={addressValue}
        onChange={handleChange}
        inputRef={inputRef}
        autoComplete="off"
        sx={{
          fontFamily,
          "& .MuiOutlinedInput-notchedOutline legend": { fontFamily },
        }}
        inputProps={{ style: { fontFamily } }}
        InputLabelProps={{ style: { fontFamily } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <RoomOutlinedIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                size="small"
                onClick={handleGeolocate}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={16} />
                ) : (
                  <MyLocationIcon fontSize="small" color="action" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </div>
  );
};

export default BranchAddressSearch;
